import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Layout } from '../components/layout/Layout';
import { BackButton } from '../components/BackButton';
import { useAuth } from '../hooks/useAuth';
import {
  createProfile, getProfiles, updateProfile, deleteProfile,
  getProfileSessions, getProfileMax,
  AVATAR_COLORS, AVATAR_ICONS, AGE_RANGES, SKILL_LEVELS,
} from '../services/profileService';
import { DECKS } from '../data/stimuli';
import { sanitizeInput } from '../utils/sanitize';
import '../styles/profiles.css';

// ── Accuracy Trend Chart ──────────────────────────────────────────────────────

const AccuracyTrendChart = ({ sessions }) => {
  if (!sessions || sessions.length === 0) {
    return <div className="prof-chart-empty">No session data yet</div>;
  }

  const PAD = 20;
  const W = 400;
  const H = 100;
  const plotW = W - PAD * 2;
  const plotH = H - PAD * 2;

  const sorted = [...sessions].reverse();
  const points = sorted.map((s, i) => {
    const x = PAD + (sorted.length === 1 ? plotW / 2 : (i / (sorted.length - 1)) * plotW);
    const acc = s.accuracy ?? (s.correct / (s.trials || 1)) * 100;
    const y = PAD + plotH - (acc / 100) * plotH;
    return { x, y, acc };
  });

  const polyline = points.map(p => `${p.x},${p.y}`).join(' ');

  return (
    <div className="prof-chart-section">
      <div className="prof-chart-label">Accuracy Trend</div>
      <svg
        className="prof-chart-svg"
        viewBox={`0 0 ${W} ${H}`}
        preserveAspectRatio="none"
      >
        {[0, 50, 100].map(pct => {
          const cy = PAD + plotH - (pct / 100) * plotH;
          return (
            <g key={pct}>
              <line x1={PAD} y1={cy} x2={W - PAD} y2={cy} stroke="var(--border)" strokeWidth="1" />
              <text x={PAD - 4} y={cy + 4} fontSize="8" fill="var(--text-muted)" textAnchor="end">
                {pct}%
              </text>
            </g>
          );
        })}
        {points.length > 1 && (
          <polyline
            points={polyline}
            fill="none"
            stroke="var(--brand)"
            strokeWidth="1.5"
          />
        )}
        {points.map((p, i) => (
          <circle key={i} cx={p.x} cy={p.y} r="3" fill="var(--brand)" />
        ))}
      </svg>
    </div>
  );
};

// ── Profile Modal ─────────────────────────────────────────────────────────────

const ProfileModal = ({ initial, onSave, onClose, accountType }) => {
  const [nickname, setNickname] = useState(initial?.nickname ?? '');
  const [icon, setIcon]         = useState(initial?.icon ?? AVATAR_ICONS[0]);
  const [color, setColor]       = useState(initial?.color ?? AVATAR_COLORS[0]);
  const [ageRange, setAgeRange] = useState(initial?.ageRange ?? '');
  const [skillLevel, setSkillLevel] = useState(initial?.skillLevel ?? '');
  const [saving, setSaving]     = useState(false);
  const [error, setError]       = useState('');

  const handleSave = async () => {
    const clean = sanitizeInput(nickname).trim();
    if (!clean) { setError('Nickname is required.'); return; }
    setSaving(true);
    await onSave({ nickname: clean, icon, color, ageRange, skillLevel });
    setSaving(false);
  };

  return (
    <div className="prof-modal-overlay" onClick={onClose}>
      <div className="prof-modal" onClick={e => e.stopPropagation()}>
        <div className="prof-modal-header">
          <h2 className="prof-modal-title">{initial ? 'Edit Profile' : 'New Session Profile'}</h2>
          <button className="prof-modal-close" onClick={onClose}>✕</button>
        </div>

        <div className="prof-privacy-note">
          Use a nickname or code — never enter a full name or identifying information.
        </div>

        <label className="prof-field-label">Nickname / Code <span className="prof-required">*</span></label>
        <input
          className="prof-input"
          placeholder="e.g. Learner A, Red Team, T7"
          value={nickname}
          maxLength={30}
          onChange={e => { setNickname(e.target.value); setError(''); }}
        />
        {error && <div className="prof-field-error">{error}</div>}

        <label className="prof-field-label">Icon</label>
        <div className="prof-icon-grid">
          {AVATAR_ICONS.map(ic => (
            <button
              key={ic}
              className={`prof-icon-btn${icon === ic ? ' selected' : ''}`}
              onClick={() => setIcon(ic)}
            >
              {ic}
            </button>
          ))}
        </div>

        <label className="prof-field-label">Color</label>
        <div className="prof-color-row">
          {AVATAR_COLORS.map(c => (
            <button
              key={c}
              className={`prof-color-swatch${color === c ? ' selected' : ''}`}
              style={{ background: c }}
              onClick={() => setColor(c)}
            />
          ))}
        </div>

        <label className="prof-field-label">Age Range <span className="prof-optional">(optional)</span></label>
        <div className="prof-toggle-row">
          {AGE_RANGES.map(r => (
            <button
              key={r.value}
              className={`prof-toggle-pill${ageRange === r.value ? ' selected' : ''}`}
              onClick={() => setAgeRange(ageRange === r.value ? '' : r.value)}
            >
              {r.label}
            </button>
          ))}
        </div>

        <label className="prof-field-label">Skill Level <span className="prof-optional">(optional)</span></label>
        <div className="prof-toggle-row">
          {SKILL_LEVELS.map(s => (
            <button
              key={s.value}
              className={`prof-toggle-pill${skillLevel === s.value ? ' selected' : ''}`}
              onClick={() => setSkillLevel(skillLevel === s.value ? '' : s.value)}
            >
              {s.label}
            </button>
          ))}
        </div>

        <div className="prof-modal-actions">
          <button className="prof-btn-secondary" onClick={onClose}>Cancel</button>
          <button className="prof-btn-primary" onClick={handleSave} disabled={saving}>
            {saving ? 'Saving…' : 'Save Profile'}
          </button>
        </div>
      </div>
    </div>
  );
};

// ── Delete Modal ──────────────────────────────────────────────────────────────

const DeleteModal = ({ profile, onConfirm, onClose }) => (
  <div className="prof-delete-overlay" onClick={onClose}>
    <div className="prof-delete-modal" onClick={e => e.stopPropagation()}>
      <h2 className="prof-delete-title">Delete Profile?</h2>
      <p className="prof-delete-body">
        Delete <strong>{profile.nickname}</strong>? This will remove all saved sessions for this profile.
        This cannot be undone.
      </p>
      <div className="prof-modal-actions">
        <button className="prof-btn-secondary" onClick={onClose}>Cancel</button>
        <button className="prof-btn-danger" onClick={onConfirm}>Delete</button>
      </div>
    </div>
  </div>
);

// ── Profile Detail View ───────────────────────────────────────────────────────

const ProfileDetailView = ({ profile, sessions, accountType, onEdit, onDelete, onBack, userId, onAssigned }) => {
  const [showAssign, setShowAssign]             = useState(false);
  const [draftDeck, setDraftDeck]               = useState(profile.assignment?.deckName ?? DECKS[0]?.name ?? '');
  const [draftTrials, setDraftTrials]           = useState(profile.assignment?.trialCount ?? 10);
  const [savingAssign, setSavingAssign]         = useState(false);
  const totalSessions = sessions.length;
  const accuracies = sessions
    .filter(s => s.trials > 0)
    .map(s => s.accuracy ?? Math.round((s.correct / s.trials) * 100));

  const bestAcc   = accuracies.length ? Math.max(...accuracies) : null;
  const avgAcc    = accuracies.length ? Math.round(accuracies.reduce((a, b) => a + b, 0) / accuracies.length) : null;
  const lastAcc   = accuracies.length ? accuracies[0] : null;

  const deckMap = {};
  sessions.forEach(s => {
    if (!s.deckName) return;
    if (!deckMap[s.deckName]) deckMap[s.deckName] = { count: 0, total: 0 };
    deckMap[s.deckName].count += 1;
    deckMap[s.deckName].total += s.accuracy ?? Math.round((s.correct / (s.trials || 1)) * 100);
  });

  const handleCSV = () => {
    const rows = [
      ['Date', 'Deck', 'Trials', 'Correct', 'Incorrect', 'Accuracy %'],
      ...sessions.map(s => {
        const acc = s.accuracy ?? Math.round((s.correct / (s.trials || 1)) * 100);
        const d = s.completedAt?.toDate ? s.completedAt.toDate() : new Date(s.completedAt);
        return [
          d.toLocaleDateString(),
          s.deckName ?? '',
          s.trials ?? '',
          s.correct ?? '',
          s.incorrect ?? '',
          acc,
        ];
      }),
    ];
    const csv = rows.map(r => r.join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `sessions-${profile.nickname.replace(/\s+/g, '-')}-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const accColor = (acc) => {
    if (acc >= 80) return 'good';
    if (acc >= 60) return 'ok';
    return 'low';
  };

  const handleSaveAssignment = async () => {
    if (!draftDeck) return;
    setSavingAssign(true);
    const assignment = { deckName: draftDeck, trialCount: draftTrials };
    await updateProfile(userId, profile.id, { assignment });
    onAssigned(assignment);
    setShowAssign(false);
    setSavingAssign(false);
  };

  const handleClearAssignment = async () => {
    await updateProfile(userId, profile.id, { assignment: null });
    onAssigned(null);
  };

  return (
    <div className="prof-detail">
      <button className="prof-back-btn" onClick={onBack}>← All Profiles</button>

      <div className="prof-detail-header">
        <div className="prof-detail-avatar" style={{ background: profile.color }}>
          {profile.icon}
        </div>
        <div className="prof-detail-info">
          <h2 className="prof-detail-name">{profile.nickname}</h2>
          <div className="prof-tags">
            {profile.ageRange && (
              <span className="prof-tag">
                {AGE_RANGES.find(r => r.value === profile.ageRange)?.label}
              </span>
            )}
            {profile.skillLevel && (
              <span className="prof-tag">
                {SKILL_LEVELS.find(s => s.value === profile.skillLevel)?.label}
              </span>
            )}
          </div>
        </div>
        <div className="prof-detail-actions">
          <button className="prof-btn-secondary" onClick={onEdit}>Edit</button>
          <button className="prof-btn-danger-outline" onClick={onDelete}>Delete</button>
        </div>
      </div>

      <div className="prof-stats-row">
        <div className="prof-stat-box">
          <div className="prof-stat-box-label">Total Sessions</div>
          <div className="prof-stat-box-val">{totalSessions}</div>
        </div>
        <div className="prof-stat-box">
          <div className="prof-stat-box-label">Best Session</div>
          <div className="prof-stat-box-val accent">{bestAcc !== null ? `${bestAcc}%` : '—'}</div>
        </div>
        <div className="prof-stat-box">
          <div className="prof-stat-box-label">Most Recent</div>
          <div className="prof-stat-box-val accent">{lastAcc !== null ? `${lastAcc}%` : '—'}</div>
        </div>
        <div className="prof-stat-box">
          <div className="prof-stat-box-label">Avg Accuracy</div>
          <div className="prof-stat-box-val accent">{avgAcc !== null ? `${avgAcc}%` : '—'}</div>
        </div>
      </div>

      <AccuracyTrendChart sessions={sessions} />

      {Object.keys(deckMap).length > 0 && (
        <div className="prof-deck-section">
          <h3 className="prof-section-title">Deck Breakdown</h3>
          <table className="prof-deck-table">
            <thead>
              <tr><th>Deck</th><th>Sessions</th><th>Avg Accuracy</th></tr>
            </thead>
            <tbody>
              {Object.entries(deckMap).map(([name, d]) => (
                <tr key={name}>
                  <td>{name}</td>
                  <td>{d.count}</td>
                  <td>{Math.round(d.total / d.count)}%</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <div className="prof-history-section">
        <div className="prof-history-header">
          <h3 className="prof-section-title">Session History</h3>
          {sessions.length > 0 && (
            <button className="prof-btn-secondary prof-export-btn" onClick={handleCSV}>
              Export CSV
            </button>
          )}
        </div>
        {sessions.length === 0 ? (
          <p className="prof-empty-note">No sessions recorded yet.</p>
        ) : (
          <table className="prof-history-table">
            <thead>
              <tr><th>Date</th><th>Deck</th><th>Trials</th><th>Correct</th><th>Incorrect</th><th>Accuracy</th></tr>
            </thead>
            <tbody>
              {sessions.map(s => {
                const acc = s.accuracy ?? Math.round((s.correct / (s.trials || 1)) * 100);
                const d = s.completedAt?.toDate ? s.completedAt.toDate() : new Date(s.completedAt);
                return (
                  <tr key={s.id}>
                    <td>{d.toLocaleDateString()}</td>
                    <td>{s.deckName ?? '—'}</td>
                    <td>{s.trials ?? '—'}</td>
                    <td>{s.correct ?? '—'}</td>
                    <td>{s.incorrect ?? '—'}</td>
                    <td><span className={`prof-history-acc ${accColor(acc)}`}>{acc}%</span></td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {/* ── Assigned Practice ─────────────────────────────── */}
      <div className="prof-assign-section">
        <div className="prof-history-header">
          <h3 className="prof-section-title">Assigned Practice</h3>
          <button
            className="prof-btn-secondary"
            style={{ fontSize: 12, height: 30, padding: '0 12px' }}
            onClick={() => setShowAssign(v => !v)}
          >
            {profile.assignment?.deckName ? 'Change' : 'Assign Work'}
          </button>
        </div>

        {showAssign ? (
          <div className="prof-assign-form">
            <label className="prof-field-label">Deck</label>
            <select
              className="prof-input"
              value={draftDeck}
              onChange={e => setDraftDeck(e.target.value)}
            >
              {DECKS.map(d => (
                <option key={d.name} value={d.name}>{d.emoji} {d.name}</option>
              ))}
            </select>

            <label className="prof-field-label">Number of Trials</label>
            <select
              className="prof-input"
              value={draftTrials}
              onChange={e => setDraftTrials(Number(e.target.value))}
            >
              {[5, 10, 15, 20].map(n => (
                <option key={n} value={n}>{n} trials</option>
              ))}
            </select>

            <div className="prof-modal-actions" style={{ paddingTop: 12 }}>
              <button className="prof-btn-secondary" onClick={() => setShowAssign(false)}>Cancel</button>
              <button className="prof-btn-primary" onClick={handleSaveAssignment} disabled={savingAssign}>
                {savingAssign ? 'Saving…' : 'Save Assignment'}
              </button>
            </div>
          </div>
        ) : profile.assignment?.deckName ? (
          <div className="prof-assign-current">
            <div className="prof-assign-deck-info">
              <span className="prof-assign-emoji">
                {DECKS.find(d => d.name === profile.assignment.deckName)?.emoji}
              </span>
              <div>
                <div className="prof-assign-deck-name">{profile.assignment.deckName}</div>
                <div className="prof-assign-meta">{profile.assignment.trialCount} trials per session</div>
              </div>
            </div>
            <button
              className="prof-btn-danger-outline"
              style={{ fontSize: 12, height: 30, padding: '0 12px' }}
              onClick={handleClearAssignment}
            >
              Clear
            </button>
          </div>
        ) : (
          <p className="prof-empty-note">
            No activity assigned. Click "Assign Work" to set up independent practice for this learner.
          </p>
        )}
      </div>
    </div>
  );
};

// ── ProfilesPage ──────────────────────────────────────────────────────────────

export const ProfilesPage = () => {
  const navigate = useNavigate();
  const { currentUser, userData } = useAuth();
  const accountType = userData?.accountType ?? 'full';
  const maxProfiles = getProfileMax(accountType);

  const [profiles, setProfiles]             = useState([]);
  const [loading, setLoading]               = useState(true);
  const [selectedProfile, setSelectedProfile] = useState(null);
  const [profileSessions, setProfileSessions] = useState([]);
  const [sessionsLoading, setSessionsLoading] = useState(false);
  const [showModal, setShowModal]           = useState(false);
  const [editingProfile, setEditingProfile] = useState(null);
  const [deleteTarget, setDeleteTarget]     = useState(null);

  const loadProfiles = useCallback(async () => {
    if (!currentUser) return;
    setLoading(true);
    const res = await getProfiles(currentUser.uid);
    if (res.success) setProfiles(res.data);
    setLoading(false);
  }, [currentUser]);

  useEffect(() => { loadProfiles(); }, [loadProfiles]);

  const openDetail = async (profile) => {
    if (!currentUser) return;
    setSelectedProfile(profile);
    setSessionsLoading(true);
    const res = await getProfileSessions(currentUser.uid, profile.id, accountType);
    if (res.success) setProfileSessions(res.data);
    setSessionsLoading(false);
  };

  const handleCreate = async (data) => {
    const res = await createProfile(currentUser.uid, data, profiles.length, accountType);
    if (res.success) { await loadProfiles(); setShowModal(false); }
  };

  const handleEdit = async (data) => {
    const res = await updateProfile(currentUser.uid, editingProfile.id, data);
    if (res.success) {
      await loadProfiles();
      setEditingProfile(null);
      setShowModal(false);
      if (selectedProfile?.id === editingProfile.id) {
        setSelectedProfile({ ...editingProfile, ...data });
      }
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    const activeId = localStorage.getItem('sc-active-profile-id');
    if (activeId === deleteTarget.id) localStorage.removeItem('sc-active-profile-id');
    await deleteProfile(currentUser.uid, deleteTarget.id);
    setDeleteTarget(null);
    if (selectedProfile?.id === deleteTarget.id) setSelectedProfile(null);
    await loadProfiles();
  };

  const atLimit = profiles.length >= maxProfiles;

  if (loading) {
    return (
      <Layout>
        <div className="prof-page-loading">Loading profiles…</div>
      </Layout>
    );
  }

  if (selectedProfile) {
    return (
      <Layout>
        {sessionsLoading ? (
          <div className="prof-page-loading">Loading sessions…</div>
        ) : (
          <ProfileDetailView
            profile={selectedProfile}
            sessions={profileSessions}
            accountType={accountType}
            userId={currentUser?.uid}
            onEdit={() => { setEditingProfile(selectedProfile); setShowModal(true); }}
            onDelete={() => setDeleteTarget(selectedProfile)}
            onBack={() => setSelectedProfile(null)}
            onAssigned={(assignment) =>
              setSelectedProfile(prev => ({ ...prev, assignment }))
            }
          />
        )}
        {showModal && (
          <ProfileModal
            initial={editingProfile}
            onSave={handleEdit}
            onClose={() => { setShowModal(false); setEditingProfile(null); }}
            accountType={accountType}
          />
        )}
        {deleteTarget && (
          <DeleteModal
            profile={deleteTarget}
            onConfirm={handleDelete}
            onClose={() => setDeleteTarget(null)}
          />
        )}
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="prof-page">
        <BackButton />
        <div className="prof-page-header">
          <div>
            <h1 className="prof-page-title">Session Profiles</h1>
            <p className="prof-page-subtitle">
              Track progress per learner using initials or codes — never enter real names or identifying information (HIPAA).
            </p>
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <button
              className="prof-btn-secondary"
              onClick={() => navigate('/play')}
            >
              Open Play Mode
            </button>
            <button
              className="prof-btn-primary"
              onClick={() => { setEditingProfile(null); setShowModal(true); }}
              disabled={atLimit}
              title={atLimit ? `Limit reached (${maxProfiles} profiles)` : undefined}
            >
              + New Profile
            </button>
          </div>
        </div>

        {atLimit && (
          <div className="prof-limit-note">
            {accountType === 'trial'
              ? 'Trial accounts support 1 profile. Upgrade for up to 20.'
              : `You've reached the ${maxProfiles}-profile limit.`}
          </div>
        )}

        {profiles.length === 0 ? (
          <div className="prof-empty-state">
            <h2 className="prof-empty-title">No profiles yet</h2>
            <p className="prof-empty-body">
              Create a Session Profile to track progress for each learner.
            </p>
            <button className="prof-btn-primary" onClick={() => setShowModal(true)}>
              Create First Profile
            </button>
          </div>
        ) : (
          <div className="prof-grid">
            {profiles.map(p => (
              <div
                key={p.id}
                className="prof-card"
                role="button"
                tabIndex={0}
                onClick={() => openDetail(p)}
                onKeyDown={e => e.key === 'Enter' && openDetail(p)}
              >
                <div className="prof-avatar" style={{ background: p.color }}>{p.icon}</div>
                <div className="prof-nickname">{p.nickname}</div>
                <div className="prof-tags">
                  {p.ageRange && (
                    <span className="prof-tag">
                      {AGE_RANGES.find(r => r.value === p.ageRange)?.label}
                    </span>
                  )}
                  {p.skillLevel && (
                    <span className="prof-tag">
                      {SKILL_LEVELS.find(s => s.value === p.skillLevel)?.label}
                    </span>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {showModal && (
        <ProfileModal
          initial={editingProfile}
          onSave={editingProfile ? handleEdit : handleCreate}
          onClose={() => { setShowModal(false); setEditingProfile(null); }}
          accountType={accountType}
        />
      )}
      {deleteTarget && (
        <DeleteModal
          profile={deleteTarget}
          onConfirm={handleDelete}
          onClose={() => setDeleteTarget(null)}
        />
      )}
    </Layout>
  );
};
